import React from "react";
import Select from "react-select";
import Webcam from "react-webcam";
import { Button, Col, Input, Progress, Row } from "reactstrap";
import Box from "./Box";
import Header from "./Header";
import ButtonSpinner from "../../../Forms/ButtonSpinner";

/**
 * Shared layout for LabelOCR and MetaOCR.
 * Renders: Box, Header, device selector, camera frame, progress bar, optional zoom/regex pickers, results, Back/Next.
 */
export default function ScanStepLayout({
    title,
    instructions,
    side,
    itemNumber = "label",
    devices,
    selectedDeviceId,
    onDeviceChange,
    webcamRef,
    videoConstraints,
    onCameraError,
    onUserMedia,
    processedImage,
    isToggled,
    countdown,
    showProgress = true,
    waitingSeconds = 7,
    onOverlayClick = null,
    cordCylinder = false,
    zoomSlider = null,
    regexPickers = null,
    results,
    jobQueue = [],
    camera_results = [],
    canvasRef,
    goBack,
    goNext,
    nextDisabled,
    nextLoading = false,
    traceback,
}) {
    const handleDeviceChange = (opt) => {
        onDeviceChange?.(opt.value);
        if (opt?.value) localStorage.setItem("selectedCameraDeviceId", opt.value);
    };

    const selectValue = devices?.find(d => d.deviceId === selectedDeviceId)
        ? { label: devices.find(d => d.deviceId === selectedDeviceId).label, value: selectedDeviceId }
        : null;

    return (
        <Box>
            <Header name={title} />
            <div>
                {instructions}
                {devices?.length > 1 && (
                    <div className="pb-3">
                        <Select
                            value={selectValue}
                            onChange={handleDeviceChange}
                            clearable={false}
                            options={devices.map(d => ({ label: d.label, value: d.deviceId }))}
                        />
                    </div>
                )}
                <div className={`position-relative bg-light ocr-camera -${side} -${itemNumber}`}>
                    <div className="camera">
                        <Webcam
                            onUserMediaError={onCameraError}
                            audio={false}
                            ref={webcamRef}
                            screenshotFormat="image/webp"
                            forceScreenshotSourceSize
                            onUserMedia={onUserMedia}
                            screenshotQuality={1}
                            imageSmoothing={false}
                            videoConstraints={videoConstraints}
                            height="100%"
                            width="100%"
                            style={{ display: "block" }}
                        />
                    </div>
                    {isToggled && processedImage && (
                        <div className="ocr-camera__processed-image"><img alt="preProcessed" src={processedImage} /></div>
                    )}
                    <div
                        className="ocr-camera__overlay"
                        onClick={onOverlayClick ?? undefined}
                        style={onOverlayClick ? undefined : { pointerEvents: "none" }}
                    >
                        {cordCylinder && (
                            <>
                                <div style={{
                                    position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                                    backgroundColor: "rgba(0,0,0,0.3)", pointerEvents: "none", zIndex: 0,
                                    clipPath: "polygon(0% 0%, 0% 100%, 20% 100%, 20% 35%, 80% 35%, 80% 65%, 20% 65%, 20% 100%, 100% 100%, 100% 0%)",
                                }} />
                                <div style={{
                                    position: "absolute", top: "35%", left: "20%", width: "60%", height: "30%",
                                    border: "2px solid #c30a14", boxSizing: "border-box", zIndex: 1,
                                    display: "flex", justifyContent: "center", alignItems: "center",
                                }}>
                                    <div style={{
                                        position: "absolute", width: 50, height: 50,
                                        borderLeft: "2px solid black", borderBottom: "2px solid black",
                                        transform: "translate(-50%, -50%)", top: "50%", left: "50%",
                                    }} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {showProgress && (
                    <div className="pt-1">
                        <Progress value={countdown * (100 / (waitingSeconds * 2))} animated>
                            Try for {Math.floor(countdown / 2)}s
                        </Progress>
                    </div>
                )}
                {zoomSlider && (
                    <div className="py-1">
                        <Input
                            type="range"
                            min={zoomSlider.min}
                            max={zoomSlider.max}
                            step={(zoomSlider.max - zoomSlider.min) / 5}
                            value={zoomSlider.value}
                            onChange={e => zoomSlider.onChange(e.target.value)}
                        />
                    </div>
                )}
                {regexPickers && Object.keys(regexPickers.regexParts.AB || {}).length > 0 && (
                    <div className="pt-2">
                        {Object.keys(regexPickers.regexParts.AB)
                            .sort((a, b) => (a === regexPickers.selectedRegexParts.AB ? -1 : b === regexPickers.selectedRegexParts.AB ? 1 : (regexPickers.regexParts.AB[b] || 0) - (regexPickers.regexParts.AB[a] || 0)))
                            .slice(0, 16)
                            .map(key => (
                                <Button key={key} className="me-1 mb-1"
                                    color={regexPickers.selectedRegexParts.AB === key ? "primary" : "light"}
                                    onClick={() => regexPickers.setSelectedRegexParts(prev => ({ ...prev, AB: key }))}>
                                    {key}
                                </Button>
                            ))}
                    </div>
                )}
                {regexPickers && Object.keys(regexPickers.regexParts.the_number || {}).length > 0 && (
                    <div className="pt-2">
                        {Object.keys(regexPickers.regexParts.the_number).slice(0, 16).map(key => (
                            <Button key={key} className="me-1 mb-1"
                                color={regexPickers.selectedRegexParts.the_number === key ? "primary" : "light"}
                                onClick={() => regexPickers.setSelectedRegexParts(prev => ({ ...prev, the_number: key }))}>
                                {key}
                            </Button>
                        ))}
                    </div>
                )}
                {isToggled && (
                    <div className="d-flex text-small">
                        <div><div>Jobs: {jobQueue.length}</div>{results}</div>
                        <div><pre className="text-end w-100">{camera_results.map((t, i) => <div key={i}>{t}</div>)}</pre></div>
                    </div>
                )}
                {!isToggled && results}
                <canvas ref={canvasRef} width="960" height="720" style={{ position: "absolute", opacity: 0, pointerEvents: "none", transform: "scale(0)" }} />
                <Row className="pt-3">
                    <Col><Button color="secondary" className="w-100" onClick={goBack}>Back</Button></Col>
                    <Col>
                        <ButtonSpinner loading={nextLoading} color="primary" className="w-100" onClick={goNext} disabled={nextDisabled}>Next</ButtonSpinner>
                    </Col>
                </Row>
                {traceback && (
                    <Row className="pt-3 opacity-30">
                        <Col><span className="text-small text-muted">Traceback code: {traceback}</span></Col>
                    </Row>
                )}
            </div>
        </Box>
    );
}
